/**
 * Hydrates marketing entry signals into Redux on boot.
 * Reads from URL params and stashCookie after promo middleware persists the super cookie.
 */
import { setEntrySignalAction } from 'stash/actions/entrySignal';
import { STASH_INIT } from 'stash/actions/init';
import { getURLParam } from 'stash/utils';
import { readIntroFaIntent } from 'stash/utils/introIntent';
import { getSuperCookie } from 'stash/utils/superCookie';
import type { Middleware } from 'redux';

function resolveEntrySignalFromUrlAndSuperCookie(): {
	theme?: string;
	channel?: string;
	promoCode?: string;
	mixName?: string;
	prompt?: string;
	intent?: string;
} {
	const superCookie = getSuperCookie();
	const theme = getURLParam('theme') || superCookie?.theme;
	const channel = getURLParam('channel') || superCookie?.channel;
	const mixName = getURLParam('mixName') || superCookie?.mixName;
	const prompt = getURLParam('prompt') || superCookie?.prompt;
	const intent = theme === 'intro-fa' ? readIntroFaIntent() : undefined;
	// `stash_code_v2` is the promo code web-reg posts to Juncture's entry-signal
	// endpoint (useUserCreateAttribution), which Juncture looks up as the campaign
	// tag that enrolls the user. Hydrating it lets eligibility gates key on the
	// signal that actually grants the promo rather than the decorative `theme`.
	const promoCode = getURLParam('stash_code_v2') || superCookie?.promoCode;

	return {
		...(theme ? { theme } : {}),
		...(channel ? { channel } : {}),
		...(promoCode ? { promoCode } : {}),
		...(mixName ? { mixName } : {}),
		...(prompt ? { prompt } : {}),
		...(intent ? { intent } : {}),
	};
}

const entrySignal: Middleware = (store) => (next) => (rawAction) => {
	const action = rawAction as { type: string };
	if (action.type === STASH_INIT) {
		const entrySignalPayload = resolveEntrySignalFromUrlAndSuperCookie();
		if (Object.keys(entrySignalPayload).length > 0) {
			store.dispatch(setEntrySignalAction(entrySignalPayload));
		}
	}

	return next(rawAction);
};

export default entrySignal;
